// Conexión de Instagram por la API oficial, por marca. El token nunca pasa por
// acá: lo guardan y lo leen las edges `ig-callback` e `ig-conexion`.
// Ver ADR-2026-09-24-instagram-conexion-oficial.
//
// Flujo:
//   1. click → `abrirVentanaDeInstagram()` (SINCRÓNICO, antes de cualquier await)
//   2. `conectarInstagramEnVentana(brandId, popup)`:
//      ig-conexion/iniciar → URL de Instagram → la persona acepta →
//      ig-callback deja la conexión en espera → ig-conectado.html avisa el claim →
//      ig-conexion/finalizar lo reclama con la sesión de la persona.

import { supabase } from "./supabase";
import {
  CLAVE_AVISO,
  ErrorDeInstagram,
  EVENTO_CAMBIO_DE_INSTAGRAM,
  leerAvisoDeConexion,
  type AvisoDeConexion,
} from "./instagramConexionLogica";

export {
  ErrorDeInstagram,
  EVENTO_CAMBIO_DE_INSTAGRAM,
  INSTAGRAM_OFICIAL_PARA_TODOS,
  mensajeDeErrorDeInstagram,
  veInstagramOficial,
} from "./instagramConexionLogica";

function avisarCambio() {
  try {
    window.dispatchEvent(new Event(EVENTO_CAMBIO_DE_INSTAGRAM));
  } catch {
    /* noop */
  }
}

/** Lo que tarda como máximo alguien en loguearse en Instagram (el state vence a los 10 min). */
const ESPERA_MAXIMA_MS = 10 * 60 * 1000;

/**
 * Abre la ventana del login. **Llamar SIEMPRE de forma sincrónica dentro del
 * handler del click**, nunca después de un `await`: Safari y todos los
 * navegadores de iPhone la bloquean (ADR-2026-08-07-tiendanube-popup-safari).
 */
export function abrirVentanaDeInstagram(): Window | null {
  return window.open("/ig-conectando.html", "ig_connect", "width=620,height=760");
}

type Invocacion = Record<string, unknown>;

/** Llama a `ig-conexion` y convierte sus errores en `ErrorDeInstagram`. */
async function invocar<T>(body: Invocacion): Promise<T> {
  const { data, error } = await supabase.functions.invoke("ig-conexion", { body });
  if (!error) return data as T;
  let cuerpo: { code?: string; marca?: string | null; username?: string | null } = {};
  try {
    cuerpo = (await (error as { context?: Response }).context?.json()) ?? {};
  } catch {
    /* sin cuerpo: error de red */
  }
  throw new ErrorDeInstagram(cuerpo.code || "red", { marca: cuerpo.marca, username: cuerpo.username });
}

function cerrar(popup: Window | null | undefined) {
  try {
    popup?.close();
  } catch {
    /* noop */
  }
}

function olvidarAviso() {
  try {
    localStorage.removeItem(CLAVE_AVISO);
  } catch {
    /* noop */
  }
}

/**
 * Espera el aviso de `ig-conectado.html`: por postMessage (lo normal) o por
 * localStorage (si el navegador perdió el `opener`). Rechaza si cierran la
 * ventana antes o si se agota el tiempo.
 */
function esperarAviso(popup: Window): Promise<AvisoDeConexion> {
  return new Promise((resolve, reject) => {
    let listo = false;

    const terminar = (aviso: AvisoDeConexion | null, err?: ErrorDeInstagram) => {
      if (listo) return;
      listo = true;
      window.removeEventListener("message", alMensaje);
      window.removeEventListener("storage", alStorage);
      clearInterval(vigia);
      clearTimeout(limite);
      olvidarAviso();
      if (aviso) resolve(aviso);
      else reject(err);
    };

    const delStorage = () => {
      try {
        return leerAvisoDeConexion(localStorage.getItem(CLAVE_AVISO));
      } catch {
        return null;
      }
    };

    const alMensaje = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      const aviso = leerAvisoDeConexion(e.data);
      if (aviso) terminar(aviso);
    };
    const alStorage = (e: StorageEvent) => {
      if (e.key !== CLAVE_AVISO) return;
      const aviso = leerAvisoDeConexion(e.newValue);
      if (aviso) terminar(aviso);
    };

    window.addEventListener("message", alMensaje);
    window.addEventListener("storage", alStorage);

    // Si cierran la ventana, puede que el aviso ya esté en localStorage
    // (el evento `storage` no siempre llega antes que el cierre).
    const vigia = setInterval(() => {
      if (!popup.closed) return;
      const aviso = delStorage();
      terminar(aviso, aviso ? undefined : new ErrorDeInstagram("ventana_cerrada"));
    }, 700);

    const limite = setTimeout(() => {
      cerrar(popup);
      terminar(null, new ErrorDeInstagram("tiempo"));
    }, ESPERA_MAXIMA_MS);
  });
}

export interface InstagramConectado {
  username: string | null;
  foto: string | null;
  /** La persona no aceptó el permiso de métricas: conectada, pero el agente no va a poder leerlas. */
  faltanMetricas: boolean;
}

/**
 * Conecta el Instagram de una marca sobre una ventana **ya abierta por el
 * click** (`abrirVentanaDeInstagram`). Tira `ErrorDeInstagram` con un mensaje
 * listo para mostrar.
 */
export async function conectarInstagramEnVentana(
  brandId: string | null,
  popup: Window | null | undefined,
  /**
   * Pedir también el permiso de publicar (el calendario, ADR-2026-10-28-instagram-publica-el-calendario). La edge sólo
   * lo suma para dev testers mientras Meta no lo apruebe.
   */
  opciones: { publicar?: boolean } = {},
): Promise<InstagramConectado> {
  if (!popup) throw new ErrorDeInstagram("popup_bloqueado");
  olvidarAviso(); // que no se cuele el aviso de un intento anterior

  let url: string;
  try {
    ({ url } = await invocar<{ url: string }>({
      accion: "iniciar",
      brand_id: brandId,
      ...(opciones.publicar ? { publicar: true } : {}),
    }));
  } catch (e) {
    cerrar(popup);
    throw e;
  }
  if (popup.closed) throw new ErrorDeInstagram("ventana_cerrada");
  popup.location.href = url;

  const aviso = await esperarAviso(popup);
  cerrar(popup);
  if ("error" in aviso) throw new ErrorDeInstagram(aviso.error);

  const r = await invocar<{ username: string | null; foto: string | null; faltan_metricas?: boolean }>({
    accion: "finalizar",
    claim: aviso.claim,
  });
  avisarCambio();
  return { username: r.username, foto: r.foto, faltanMetricas: !!r.faltan_metricas };
}

export interface EstadoDeInstagram {
  conectada: boolean;
  /** activa | vencida | revocada; null si nunca se conectó. */
  estado: string | null;
  username: string | null;
  foto: string | null;
  venceAt: string | null;
  /** Aceptó el permiso de publicar al conectar (lo mira el calendario antes de publicar solo). */
  puedePublicar: boolean;
  /** `true` = no pudimos preguntar: `conectada: false` quiere decir "no sabemos". */
  error: boolean;
}

/** Estado de la conexión de una marca. Liviano: no le pega a Instagram. */
export async function estadoDeInstagram(brandId: string | null): Promise<EstadoDeInstagram> {
  try {
    const d = await invocar<{
      conectada?: boolean;
      estado?: string;
      username?: string | null;
      foto?: string | null;
      vence_at?: string | null;
      puede_publicar?: boolean;
    }>({ accion: "estado", brand_id: brandId });
    return {
      conectada: !!d?.conectada,
      estado: d?.estado ?? null,
      username: d?.username ?? null,
      foto: d?.foto ?? null,
      venceAt: d?.vence_at ?? null,
      puedePublicar: d?.puede_publicar === true,
      error: false,
    };
  } catch {
    return { conectada: false, estado: null, username: null, foto: null, venceAt: null, puedePublicar: false, error: true };
  }
}

/** Desconecta el Instagram de una marca (borra la conexión y el token). */
export async function desconectarInstagram(brandId: string | null): Promise<void> {
  await invocar({ accion: "desconectar", brand_id: brandId });
  avisarCambio();
}
